// Library imports
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { PageContent, PageHeader, PageWrapper, PageContainer } from 'components/common/layout_element/LayoutElement';
import { Row, Button, Stack, Card, Text, Pagination, TextInput, Icon } from "_core/components/common/UI";
import { CustomDateRangePicker, DateRadio, FitList } from "components/common/parts";
import * as Constants from "_core/configs/Constants";
import { setLoading, setErrorModal, setInitializeState, setMenuState } from "_core/slices/scene/SceneSlice";
import AdminInquiryLogListApi from "apis/AdminInquiryLogListApi";
import AdminInquiryLogDetailApi from "apis/AdminInquiryLogDetailApi";
import LogsCollectApi from "apis/LogsCollectApi";
import Encoding from 'encoding-japanese';

import './AdminUserInquiryLog.scss';

const PAGE_SIZE = 10;

const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}年${m}月${day}日 ${hh}:${mm}:${ss}`;
};

const computeRangeFromRadio = (radioValue) => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - (Number(radioValue) - 1));
    return [start, end];
};

const AdminUserInquiryLog = () => {
    const dispatch = useDispatch();
    const CallAdminInquiryLogListApi = AdminInquiryLogListApi();
    const CallAdminInquiryLogDetailApi = AdminInquiryLogDetailApi();
    const CallLogsCollectApi = LogsCollectApi();
    const { initializeState } = useSelector((store) => store.scene);

    // 表示モード: 'list' or 'detail'
    const [viewMode, setViewMode] = useState('list');

    // 一覧用
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [isListLoading, setIsListLoading] = useState(true);

    // 期間ラジオ
    const RangeLabelTypeKeys = Object.keys(Constants.RangeLabelType);
    const DispRangeLabelTypeValues = Object.values(Constants.RangeLabelType).filter(value => value !== Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
    const [dateRadioCheck, setDateRadioCheck] = useState(Constants.RANGE_TYPE_SHORT);
    const [dispCustomDate, setDispCustomDate] = useState(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
    DispRangeLabelTypeValues.push(dispCustomDate);

    // 日付範囲
    const initialRange = computeRangeFromRadio(Constants.RANGE_TYPE_SHORT);
    const [dateRange, setDateRange] = useState(initialRange);
    const [startDate, setStartDate] = useState(initialRange[0]);
    const [endDate, setEndDate] = useState(initialRange[1]);
    useEffect(() => {
        setStartDate(dateRange[0]);
        setEndDate(dateRange[1]);
    }, [dateRange]);

    // カレンダーダイアログ
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const dialogRef = useRef(null);
    const radioLabelOptions = { month: 'long', day: 'numeric' };

    // 検索
    const [searchInquiryIdInput, setSearchInquiryIdInput] = useState("");
    const [searchInquiryId, setSearchInquiryId] = useState("");

    // 一覧データ
    const [inquiryLogListData, setInquiryLogListData] = useState({
        columsize: { inquiry_id: 1, summary: 3, regdate: 1 },
        title: {
            inquiry_id: "問い合わせ番号",
            summary: "問い合わせ概要",
            regdate: "問い合わせ日時",
        },
        value: []
    });

    // 詳細データ
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailInquiry, setDetailInquiry] = useState(null);
    const [detailChatLogRaw, setDetailChatLogRaw] = useState([]);
    const [detailChatLogData, setDetailChatLogData] = useState({
        columsize: { ip_address: 1, question: 3, answer: 3, regdate: 1 },
        title: {
            ip_address: "IP / セッションID",
            question: "質問",
            answer: "返答",
            regdate: "日時",
        },
        value: []
    });

    useEffect(() => {
        dispatch(setMenuState({ isHeader: false, isMenu: true, isFooter: false, isFullScreen: false }));
    }, [dispatch]);

    useEffect(() => {
        if (initializeState !== Constants.INITIALIZE_STATE.allComplete) {
            return;
        }
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetching));
        loadList(1, startDate, endDate, searchInquiryId);
    }, [initializeState]);

    useEffect(() => {
        if (isDialogVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDialogVisible]);

    const topRef = useRef(null);
    const scrollToRef = (targetRef) => {
        console.log(targetRef.current);
        if (targetRef.current) {
            console.log("scroll started");
            targetRef.current.scrollIntoView({
                behavior: "smooth", // スムーズなスクロール
                block: "start",    // スクロール位置（'start', 'center', 'end' など）
            });
        }
    };

    const handleClickOutside = (event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
            setIsDialogVisible(false);
            setDateRange([startDate, endDate]);
            if (startDate && endDate) {
                setDispCustomDate(startDate.toLocaleDateString('ja-JP', radioLabelOptions) + "-" + endDate.toLocaleDateString('ja-JP', radioLabelOptions));
            }
        }
    };

    const buildRequestParams = (targetPage, nextStartDate, nextEndDate, nextInquiryId) => {
        const params = {
            page: targetPage,
            page_size: PAGE_SIZE,
            inquiry_id: nextInquiryId || "",
            range_start_year: 0,
            range_start_month: 0,
            range_start_day: 0,
            range_end_year: 0,
            range_end_month: 0,
            range_end_day: 0,
        };
        if (nextStartDate) {
            params.range_start_year = nextStartDate.getFullYear();
            params.range_start_month = nextStartDate.getMonth() + 1;
            params.range_start_day = nextStartDate.getDate();
        }
        if (nextEndDate) {
            params.range_end_year = nextEndDate.getFullYear();
            params.range_end_month = nextEndDate.getMonth() + 1;
            params.range_end_day = nextEndDate.getDate();
        }
        return params;
    };

    const loadList = async (targetPage, nextStartDate, nextEndDate, nextInquiryId) => {
        setIsListLoading(true);
        if (targetPage === 1) {
            dispatch(setLoading(true));
        }
        const params = buildRequestParams(targetPage, nextStartDate, nextEndDate, nextInquiryId);
        const { response } = await CallAdminInquiryLogListApi(params);

        if (response && response.error_code === 0) {
            setMaxPage(response.max_page || 1);
            buildListData(response.t_inquiry_log_list || []);
        } else {
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
            buildListData([]);
        }
        setIsListLoading(false);
        dispatch(setLoading(false));
        dispatch(setInitializeState(Constants.INITIALIZE_STATE.pageDataFetchComplete));
    };

    const buildListData = (logs) => {
        const listData = {
            columsize: { inquiry_id: 1, summary: 3, regdate: 1 },
            title: {
                inquiry_id: "問い合わせ番号",
                summary: "問い合わせ概要",
                regdate: "日時",
            },
            value: []
        };
        logs.forEach((log) => {
            const rowData = {
                inquiry_id: log.inquiry_id,
                summary: log.summary,
                regdate: formatDateTime(log.regdate),
            };
            const onClick = () => {
                openDetail(log.inquiry_id);
            };
            listData.value.push({
                data: rowData,
                title: {},
                onclick: onClick,
            });
        });
        setInquiryLogListData(listData);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        loadList(newPage, startDate, endDate, searchInquiryId);
    };

    const changeDateRadio = (value) => {
        setDateRadioCheck(value);
        setDispCustomDate(Constants.RangeLabelType[Constants.RANGE_TYPE_CUSTOM]);
        if (Number(value) === Constants.RANGE_TYPE_CUSTOM) {
            return;
        }
        const [nextStart, nextEnd] = computeRangeFromRadio(value);
        setDateRange([nextStart, nextEnd]);
        setPage(1);
        loadList(1, nextStart, nextEnd, searchInquiryId);
    };

    const clickDateRadio = (value) => {
        if (Number(value) === Constants.RANGE_TYPE_CUSTOM) {
            setIsDialogVisible(true);
        }
    };

    const handleDateChange = (update) => {
        setDateRange(update);
    };

    const handleCancel = () => {
        setDateRange([null, null]);
    };

    const handleUpdate = () => {
        setIsDialogVisible(false);
        if (startDate && endDate) {
            setDispCustomDate(startDate.toLocaleDateString('ja-JP', radioLabelOptions) + "-" + endDate.toLocaleDateString('ja-JP', radioLabelOptions));
        }
        setPage(1);
        loadList(1, startDate, endDate, searchInquiryId);
    };

    const handleSearch = () => {
        setSearchInquiryId(searchInquiryIdInput);
        setPage(1);
        loadList(1, startDate, endDate, searchInquiryIdInput);
    };

    // ブラウザの戻る (マウスのbackボタン含む) で一覧に戻れるようにする
    useEffect(() => {
        const handlePopState = () => {
            // 詳細表示中のときだけ一覧に戻す。デフォルトの遷移は抑止しない (詳細表示前に pushState で履歴を1個追加しているため、戻ると元のページに到達する)
            setViewMode((prev) => {
                if (prev === 'detail') {
                    setDetailInquiry(null);
                    return 'list';
                }
                return prev;
            });
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // 詳細ビュー
    const openDetail = async (inquiryId) => {
        // 履歴に1ステップ積んでおき、戻る操作で一覧に戻れるようにする
        try {
            window.history.pushState({ inquiryDetail: inquiryId }, "", window.location.href);
        } catch (e) {
            console.warn("pushState failed:", e);
        }
        setViewMode('detail');
        setDetailInquiry(null);
        setDetailLoading(true);
        dispatch(setLoading(true));

        const { response } = await CallAdminInquiryLogDetailApi({ inquiry_id: inquiryId });
        dispatch(setLoading(false));
        setDetailLoading(false);

        if (response && response.error_code === 0) {
            setDetailInquiry(response.t_inquiry_log || null);
            buildDetailChatLogData(response.t_chat_log_list || []);
        } else {
            setDetailInquiry(null);
            buildDetailChatLogData([]);
            dispatch(setErrorModal({ show: true, text: response.error_detail }));
        }
    };

    const buildDetailChatLogData = (chatLogs) => {
        setDetailChatLogRaw(chatLogs);
        const listData = {
            columsize: { ip_address: 1, question: 3, answer: 3, regdate: 1 },
            title: {
                ip_address: "IP / セッションID",
                question: "質問",
                answer: "返答",
                regdate: "日時",
            },
            value: []
        };
        chatLogs.forEach((log) => {
            const rowData = {
                ip_address: (log.ip_address || "") + "\n" + (log.session_id || ""),
                question: log.question,
                answer: log.answer,
                regdate: formatDateTime(log.regdate),
            };
            listData.value.push({
                data: rowData,
                title: {},
                onclick: null,
            });
        });
        setDetailChatLogData(listData);
    };

    const generateChatLogCsv = (chatLogs) => {
        const headers = ["IP", "セッションID", "質問", "返答", "日時"];
        const csvRows = [headers.join(",")];
        chatLogs.forEach((item) => {
            const dateObject = new Date(item.regdate);
            const year = dateObject.getFullYear();
            const month = dateObject.getMonth() + 1;
            const day = dateObject.getDate();
            const hours = String(dateObject.getHours()).padStart(2, '0');
            const minutes = String(dateObject.getMinutes()).padStart(2, '0');
            const seconds = String(dateObject.getSeconds()).padStart(2, '0');
            const row = [
                item.ip_address,
                item.session_id,
                `"${item.question}"`,
                `"${item.answer}"`,
                `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`
            ];
            csvRows.push(row.join(","));
        });
        return csvRows.join("\n");
    };

    const handleChatLogCsvDownload = async () => {
        if (!detailChatLogRaw || detailChatLogRaw.length === 0) {
            return;
        }

        // 通信中の表示を出す
        dispatch(setLoading(true));

        const result = await CallLogsCollectApi({
            event_name: "inquiry_log_download"
        });

        // 通信中の表示を消す
        dispatch(setLoading(false));

        if (result.error || (result.response && result.response.error_code > 0)) {
            // エラーモーダル表示
            dispatch(setErrorModal({ show: true, text: result.response.error_detail }));
            return;
        }

        const csvData = generateChatLogCsv(detailChatLogRaw);
        const sjisArray = Encoding.convert(csvData, { to: 'SJIS', type: 'array' });
        const uint8Array = new Uint8Array(sjisArray);
        const blob = new Blob([uint8Array], { type: 'text/csv;charset=Shift_JIS' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = Constants.CHAT_LOG_CSV_FILE_NAME + "_問い合わせ番号" + detailInquiry.inquiry_id;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const backToList = () => {
        // popstate ハンドラ側で viewMode を 'list' に戻す
        if (window.history.state && window.history.state.inquiryDetail) {
            window.history.back();
        } else {
            setViewMode('list');
            setDetailInquiry(null);
        }
    };

    const renderListView = () => (
        <Stack>
            <Card className="base-card margin-bottom-32px p-4">
                <Stack className="width-full align-center spacing-16px">
                    <Row className="flex-both align-center width-full inquiry-toolbar spacing-16px">
                        <Row className="flex-left align-center inquiry-date-area">
                            <DateRadio
                                name="inquiry-range-date"
                                value={RangeLabelTypeKeys}
                                diffValue={DispRangeLabelTypeValues}
                                checked={dateRadioCheck}
                                onclick={(e) => clickDateRadio(e)}
                                onchange={(e) => changeDateRadio(e)}
                                isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                            />
                            {isDialogVisible && (
                                <div className="calender-dialog" ref={dialogRef}>
                                    <CustomDateRangePicker
                                        selected={startDate}
                                        onChange={handleDateChange}
                                        startDate={startDate}
                                        endDate={endDate}
                                        selectsRange
                                        inline
                                        handleCancel={handleCancel}
                                        handleUpdate={handleUpdate}
                                    />
                                </div>
                            )}
                        </Row>
                        <Row className="flex-right align-center spacing-8px">
                            <Row className="search-input-area align-center spacing-8px">
                                <TextInput
                                    className="search-inquiry-id"
                                    default={searchInquiryIdInput}
                                    value={searchInquiryIdInput}
                                    onchange={(e) => setSearchInquiryIdInput(e.target.value)}
                                    placeholder="問い合わせ番号"
                                />
                                <Button className="primary search-button" onClick={handleSearch}>検索</Button>
                            </Row>
                        </Row>
                    </Row>

                    <FitList
                        list={inquiryLogListData}
                        header={true}
                        scrollbar={"scrollbar"}
                        isLoading={(isListLoading || (initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete)) ? true : false}
                    />
                    <Pagination
                        totalPages={maxPage}
                        currentPage={page}
                        onChange={handlePageChange}
                        isLoading={(initializeState < Constants.INITIALIZE_STATE.pageDataFetchComplete) ? true : false}
                    />
                </Stack>
            </Card>
        </Stack>
    );

    const renderDetailView = () => (
        <Stack className="spacing-16px">
            <Row className="flex-left">
                <Button className="secondary-border back-button" onClick={backToList}>
                    <Row className="flex-center spacing-8px">
                        <Icon icon="chevron-left" size={16} />
                        <div>一覧に戻る</div>
                    </Row>
                </Button>
            </Row>

            <Card className="base-card detail-info-card">
                <Stack className="spacing-16px">
                    <Row className="detail-summary-row align-top spacing-24px">
                        <Stack className="detail-summary-cell spacing-4px">
                            <div className="detail-label text-bold">問い合わせ番号</div>
                            <div className="detail-value">{detailInquiry ? detailInquiry.inquiry_id : "-"}</div>
                        </Stack>
                        <Stack className="detail-summary-cell spacing-4px">
                            <div className="detail-label text-bold">問い合わせ概要</div>
                            <div className="detail-value detail-value-pre">{detailInquiry ? detailInquiry.summary : "-"}</div>
                        </Stack>
                        <Stack className="detail-summary-cell spacing-4px">
                            <div className="detail-label text-bold">問い合わせ日時</div>
                            <div className="detail-value">{detailInquiry ? formatDateTime(detailInquiry.regdate) : "-"}</div>
                        </Stack>
                    </Row>
                    <hr />
                    <Stack className="spacing-4px">
                        <div className="detail-label text-bold">問い合わせ内容</div>
                        <div className="detail-value detail-value-pre">{detailInquiry ? detailInquiry.mail_content : "-"}</div>
                    </Stack>
                </Stack>
            </Card>

            <Card className="base-card detail-chatlog-card">
                <Stack className="spacing-16px">
                    <Row className="flex-both width-full align-center">
                        <Stack className="spacing-4px">
                            <Text className="text-size-medium text-bold">ログ詳細</Text>
                            <Text className="text-size-small">問い合わせ発生時のセッションの対話ログを表示します。</Text>
                        </Stack>
                        <Row className="flex-right spacing-8px">
                            <Button
                                className={"dl-button " + ((detailChatLogRaw && detailChatLogRaw.length > 0) ? "primary" : "primary disabled")}
                                onClick={handleChatLogCsvDownload}
                                disabled={!detailChatLogRaw || detailChatLogRaw.length === 0}
                            >
                                <Row className="flex-center spacing-8px width-full">
                                    <Icon className="label-icon" icon="download" size={18} />
                                    <div>ダウンロード</div>
                                </Row>
                            </Button>
                        </Row>
                    </Row>
                    <FitList
                        list={detailChatLogData}
                        header={true}
                        scrollbar={"scrollbar"}
                        isLoading={detailLoading}
                    />
                </Stack>
            </Card>
        </Stack>
    );

    return (
        <PageContent pageId="AdminUserInquiryLog">
            <div ref={topRef}></div>
            <PageHeader>
                <Stack className="spacing-4px">
                    <div className="page-header-title">有人引き継ぎ一覧</div>
                    <div className="page-header-detail">ユーザーから送信された有人引き継ぎ（問い合わせ）の履歴を確認できます。</div>
                </Stack>
            </PageHeader>
            <PageWrapper>
                <PageContainer>
                    {viewMode === 'list' ? renderListView() : renderDetailView()}
                    <Button className="scroll-button primary" onClick={() => {scrollToRef(topRef)}}>
                        <Icon icon="chevron-up" size={28} className="copy-icon"/>
                    </Button>
                </PageContainer>
            </PageWrapper>
        </PageContent>
    );
};

export default AdminUserInquiryLog;
